import { LabourHourFactorCountingMethod } from "../../interfaces/projectLabourHourCommitment";
/** Avoid JSON `null` from `NaN` when encoding `target_value` / `target_value_realised`. */
const encodeFiniteNumber = (value, fallback) => {
    if (value === null || value === undefined) {
        return fallback;
    }
    const n = typeof value === "number" ? value : Number(value);
    return Number.isFinite(n) ? n : fallback;
};
/**
 * A criteria set carries only the FILTER half, so these two are a plain field
 * rename.
 *
 * NOT sorted here: `criteriaSetsExpr` in the entity handler aggregates
 * `ORDER BY ordinal`, so the array arrives in the authoring form's tab order and
 * one place owns that invariant.
 */
const decodeCriteriaSet = (criteriaSet) => ({
    abns: criteriaSet.abns,
    employmentLevel: criteriaSet.employment_level,
    excludedPostCodes: criteriaSet.excluded_post_codes,
    // No default: the criteria set column is NOT NULL with one, so a row cannot
    // arrive without a counting method. The COMMITMENT's own field below still
    // defaults, because its column predates that constraint.
    factorCountingMethod: criteriaSet.factor_counting_method,
    gender: criteriaSet.gender,
    id: criteriaSet.project_labour_hour_commitment_criteria_set_id,
    maxDistance: criteriaSet.max_distance ? criteriaSet.max_distance : undefined,
    newEntrantToConstruction: criteriaSet.new_entrant_to_construction,
    occupation: criteriaSet.occupation,
    occupationType: criteriaSet.occupation_type,
    ordinal: criteriaSet.ordinal,
    postCodes: criteriaSet.post_codes,
    socialFactors: criteriaSet.social_factors
});
const encodeCriteriaSet = (criteriaSet, commitmentId) => ({
    abns: criteriaSet.abns,
    employment_level: criteriaSet.employmentLevel,
    excluded_post_codes: criteriaSet.excludedPostCodes,
    factor_counting_method: criteriaSet.factorCountingMethod ??
        LabourHourFactorCountingMethod.AllFactorsCombined,
    gender: criteriaSet.gender,
    max_distance: criteriaSet.maxDistance,
    new_entrant_to_construction: criteriaSet.newEntrantToConstruction,
    occupation: criteriaSet.occupation,
    occupation_type: criteriaSet.occupationType,
    ordinal: criteriaSet.ordinal,
    post_codes: criteriaSet.postCodes,
    project_labour_hour_commitment_criteria_set_id: criteriaSet.id,
    project_labour_hour_commitment_id: commitmentId,
    social_factors: criteriaSet.socialFactors
});
export const decodeProjectLabourHourCommitment = (socialCommitment) => {
    return {
        abns: socialCommitment.abns,
        colour: socialCommitment.colour,
        created: new Date(socialCommitment.created),
        criteriaSets: socialCommitment.criteriaSets?.map(decodeCriteriaSet),
        employmentLevel: socialCommitment.employment_level,
        excludedPostCodes: socialCommitment.excluded_post_codes,
        factorCountingMethod: socialCommitment.factor_counting_method ??
            LabourHourFactorCountingMethod.AllFactorsCombined,
        gender: socialCommitment.gender,
        generationSource: socialCommitment.generation_source,
        id: socialCommitment.project_labour_hour_commitment_id,
        includeArchivedEmployees: socialCommitment.include_archived_employees ?? true,
        maxDistance: socialCommitment.max_distance
            ? socialCommitment.max_distance
            : undefined,
        newEntrantToConstruction: socialCommitment.new_entrant_to_construction,
        occupation: socialCommitment.occupation,
        occupationType: socialCommitment.occupation_type,
        postCodes: socialCommitment.post_codes,
        projectId: socialCommitment.project_id,
        socialFactors: socialCommitment.social_factors,
        sourceFrameworkId: socialCommitment.source_framework_id,
        sourceFrameworkTargetId: socialCommitment.source_framework_target_id,
        targetDenominator: socialCommitment.target_denominator,
        targetDescription: socialCommitment.target_description
            ? socialCommitment.target_description
            : undefined,
        targetMetricType: socialCommitment.target_metric_type,
        targetName: socialCommitment.target_type,
        targetValue: encodeFiniteNumber(socialCommitment.target_value, 0),
        targetValueRealised: encodeFiniteNumber(socialCommitment.target_value_realised, 0)
    };
};
export const encodeProjectLabourHourCommitment = (socialCommitment) => {
    return {
        abns: socialCommitment.abns,
        colour: socialCommitment.colour,
        created: socialCommitment.created,
        /**
         * Sets 2..N are stored from here. Set 1 is stored from the flat fields,
         * never from this array's first entry — see `criteriaSetFilters` in the
         * backend and `decisions.md` §28.
         */
        criteriaSets: socialCommitment.criteriaSets?.map((criteriaSet) => encodeCriteriaSet(criteriaSet, socialCommitment.id)),
        employment_level: socialCommitment.employmentLevel,
        excluded_post_codes: socialCommitment.excludedPostCodes,
        factor_counting_method: socialCommitment.factorCountingMethod ??
            LabourHourFactorCountingMethod.AllFactorsCombined,
        gender: socialCommitment.gender,
        generation_source: socialCommitment.generationSource,
        include_archived_employees: socialCommitment.includeArchivedEmployees ?? true,
        max_distance: socialCommitment.maxDistance,
        new_entrant_to_construction: socialCommitment.newEntrantToConstruction,
        occupation: socialCommitment.occupation,
        occupation_type: socialCommitment.occupationType,
        post_codes: socialCommitment.postCodes,
        project_id: socialCommitment.projectId,
        project_labour_hour_commitment_id: socialCommitment.id,
        social_factors: socialCommitment.socialFactors,
        source_framework_id: socialCommitment.sourceFrameworkId,
        source_framework_target_id: socialCommitment.sourceFrameworkTargetId,
        target_denominator: socialCommitment.targetDenominator,
        target_description: socialCommitment.targetDescription,
        target_metric_type: socialCommitment.targetMetricType,
        target_type: socialCommitment.targetName,
        target_value: encodeFiniteNumber(socialCommitment.targetValue, 0),
        target_value_realised: encodeFiniteNumber(socialCommitment.targetValueRealised, 0)
    };
};
